// DeviceIcons - Unified Icon System
// Each device type has multiple Style variants that can be selected

import React from 'react';
import { DeviceIconType, DeviceIconProps } from './types';

// Import default styles for DeviceIcon component
import { Style01_Lightbulb } from './Light';
import { Style01_Switch } from './Switch';
import { Style01_SmartOutlet } from './Outlet';
import { Style01_NetworkCheck } from './PowerMeter';
import { Style01_ThreePhase } from './PowerMeter3Phase';
import { Style01_AcUnit } from './Hvac';
import { Style01_Fan } from './Fan';
import { Style01_Thermometer } from './ThSensor';
import { Style01_SensorOccupied } from './PresenceSensor';
import { Style01_Door } from './DoorSensor';
import { Style01_RollerShades } from './Curtain';
import { Style01_Blinds } from './Blind';
import { Style01_WaterDrop } from './Dehumidifier';
import { Style01_TouchApp } from './Button';
import { Style01_Hub } from './Controller';
import { Style01_DeviceHub } from './Bridge';
import { Style01_Sensors } from './MultiSensor';
import { Style01_LightMode } from './LuxSensor';
import { Style01_Air } from './Erv';

// Re-export types
export * from './types';
export * from './config';

// Device type icon registries and styles
export { LightIconStyles, Style01_Lightbulb, Style02_LightMode } from './Light';
export { SwitchIconStyles, Style01_Switch, Style02_ToggleOn } from './Switch';
export { OutletIconStyles, Style01_SmartOutlet, Style02_Power } from './Outlet';
export { PowerMeterIconStyles, Style01_NetworkCheck, Style02_ElectricMeter } from './PowerMeter';
export { PowerMeter3PhaseIconStyles, Style01_ThreePhase, Style02_Bolt, Style03_Power, Style04_NetworkCheck as Style04_NetworkCheck3Phase } from './PowerMeter3Phase';
export { HvacIconStyles, Style01_AcUnit, Style02_AirConditioning } from './Hvac';
export { FanIconStyles, Style01_Fan } from './Fan';
export { ThSensorIconStyles, Style01_Thermometer, Style02_WaterDrop } from './ThSensor';
export { PresenceSensorIconStyles, Style01_SensorOccupied, Style02_Radio } from './PresenceSensor';
export { DoorSensorIconStyles, Style01_Door, Style02_SensorDoor } from './DoorSensor';
export { CurtainIconStyles, Style01_RollerShades, Style02_Curtains, Style03_CurtainsClosed, Style04_VerticalShades, Style05_Window } from './Curtain';
export { BlindIconStyles, Style01_Blinds, Style02_VerticalShades } from './Blind';
export { DehumidifierIconStyles, Style01_WaterDrop as Style01_DehumidifierWaterDrop } from './Dehumidifier';
export { ButtonIconStyles, Style01_TouchApp } from './Button';
export { ControllerIconStyles, Style01_Hub } from './Controller';
export { BridgeIconStyles, Style01_DeviceHub } from './Bridge';
export { MultiSensorIconStyles, Style01_Sensors } from './MultiSensor';
export { LuxSensorIconStyles, Style01_LightMode as Style01_LuxLightMode } from './LuxSensor';
export { ErvIconStyles, Style01_Air, Style02_Ventilation, Style03_AirPurifier, Style04_Fan as Style04_ErvFan } from './Erv';

/** Map of device types to their default Style01 components */
const defaultIconComponents: Record<DeviceIconType, React.ComponentType<DeviceIconProps>> = {
  light: Style01_Lightbulb,
  switch: Style01_Switch,
  outlet: Style01_SmartOutlet,
  power_meter: Style01_NetworkCheck,
  power_meter_3phase: Style01_ThreePhase,
  hvac: Style01_AcUnit,
  fan: Style01_Fan,
  th_sensor: Style01_Thermometer,
  presence_sensor: Style01_SensorOccupied,
  motion_sensor: Style01_SensorOccupied,
  door_sensor: Style01_Door,
  curtain: Style01_RollerShades,
  blind: Style01_Blinds,
  dehumidifier: Style01_WaterDrop,
  button: Style01_TouchApp,
  controller: Style01_Hub,
  bridge: Style01_DeviceHub,
  multi_sensor: Style01_Sensors,
  lux_sensor: Style01_LightMode,
  erv: Style01_Air,
};

export interface DeviceIconComponentProps extends DeviceIconProps {
  /** Device type to render icon for */
  deviceType: DeviceIconType;
  /** Style variant (e.g., 'Style01', 'Style02') */
  styleId?: string;
}

// Style registry for each device type (for dynamic selection)
const styleRegistries: Record<DeviceIconType, Record<string, React.ComponentType<DeviceIconProps>>> = {
  light: {
    Style01: Style01_Lightbulb,
    Style02: require('./Light').Style02_LightMode,
  },
  switch: {
    Style01: Style01_Switch,
    Style02: require('./Switch').Style02_ToggleOn,
  },
  outlet: {
    Style01: Style01_SmartOutlet,
    Style02: require('./Outlet').Style02_Power,
  },
  power_meter: {
    Style01: Style01_NetworkCheck,
    Style02: require('./PowerMeter').Style02_ElectricMeter,
  },
  power_meter_3phase: {
    Style01: Style01_ThreePhase,
    Style02: require('./PowerMeter3Phase').Style02_Bolt,
    Style03: require('./PowerMeter3Phase').Style03_Power,
    Style04: require('./PowerMeter3Phase').Style04_NetworkCheck,
  },
  hvac: {
    Style01: Style01_AcUnit,
    Style02: require('./Hvac').Style02_AirConditioning,
  },
  fan: {
    Style01: Style01_Fan,
    Style02: require('./Fan').Style02_Air,
    Style03: require('./Fan').Style03_Windmill,
    Style04: require('./Fan').Style04_Hvac,
  },
  th_sensor: {
    Style01: Style01_Thermometer,
    Style02: require('./ThSensor').Style02_WaterDrop,
  },
  presence_sensor: {
    Style01: Style01_SensorOccupied,
    Style02: require('./PresenceSensor').Style02_Radio,
  },
  motion_sensor: {
    Style01: Style01_SensorOccupied,
  },
  door_sensor: {
    Style01: Style01_Door,
    Style02: require('./DoorSensor').Style02_SensorDoor,
  },
  curtain: {
    Style01: Style01_RollerShades,
    Style02: require('./Curtain').Style02_Curtains,
    Style03: require('./Curtain').Style03_CurtainsClosed,
    Style04: require('./Curtain').Style04_VerticalShades,
    Style05: require('./Curtain').Style05_Window,
  },
  blind: {
    Style01: Style01_Blinds,
    Style02: require('./Blind').Style02_VerticalShades,
  },
  dehumidifier: {
    Style01: Style01_WaterDrop,
    Style02: require('./Dehumidifier').Style02_Humidity,
    Style03: require('./Dehumidifier').Style03_Dew,
    Style04: require('./Dehumidifier').Style04_Waves,
  },
  button: {
    Style01: Style01_TouchApp,
  },
  controller: {
    Style01: Style01_Hub,
  },
  bridge: {
    Style01: Style01_DeviceHub,
  },
  multi_sensor: {
    Style01: Style01_Sensors,
  },
  lux_sensor: {
    Style01: Style01_LightMode,
    Style02: require('./LuxSensor').Style02_Sunny,
    Style03: require('./LuxSensor').Style03_Brightness,
    Style04: require('./LuxSensor').Style04_WbSunny,
  },
  erv: {
    Style01: Style01_Air,
    Style02: require('./Erv').Style02_Ventilation,
    Style03: require('./Erv').Style03_AirPurifier,
    Style04: require('./Erv').Style04_Fan,
  },
};

/**
 * Get available styles for a device type
 */
export function getAvailableIconStyles(deviceType: DeviceIconType): string[] {
  const registry = styleRegistries[deviceType];
  return registry ? Object.keys(registry) : ['Style01'];
}

/**
 * DeviceIcon - Unified entry point for rendering device icons
 */
export const DeviceIcon: React.FC<DeviceIconComponentProps> = ({ 
  deviceType, 
  styleId = 'Style01',
  ...props 
}) => {
  // Get the style registry for this device type
  const registry = styleRegistries[deviceType];
  
  if (!registry) {
    // Fallback for unknown device types
    return React.createElement('span', {
      className: 'material-symbols-outlined text-slate-400',
      style: { fontSize: '20px' }
    }, 'device_unknown');
  }
  
  // Get the specific style component, fallback to Style01
  const IconComponent = registry[styleId] || registry['Style01'] || defaultIconComponents[deviceType];
  
  if (!IconComponent) {
    return React.createElement('span', {
      className: 'material-symbols-outlined text-slate-400',
      style: { fontSize: '20px' }
    }, 'device_unknown');
  }
  
  return React.createElement(IconComponent, props);
};

export default DeviceIcon;
